import { useEffect, useId, useRef, useState } from "react";
import { MAX_NEED, type Needs } from "../../src/web/types.ts";
import { api, type TripView } from "./api.ts";
import { useGrow } from "./hooks.ts";
import { useJustDone } from "./justDone.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { SettingsGroup } from "./SettingsGroup.tsx";
import { Avatar, Button, Icon } from "./souvenir.ts";
import { factLine } from "./ui.tsx";

// Diet and mobility, in Trip settings (DESIGN.md, Records, "Member", and "Private to the app, never to an AI";
// features/trips-and-members.md, "A profile per member"): what the people in the trip plan meals and walks around,
// each person's own to set, kept in the app only, never read or written by an AI. Everyone in the trip sees them
// here, me first; mine has Edit, my two fields, one write signed "on the web", undoable at once and from Updates.

export function DietAndMobility({ trip, onChanged }: { trip: TripView; onChanged: () => void }) {
  const readOnly = useReadOnly();
  const [editing, setEditing] = useState(false);
  // What I just saved, shown until the trip loads with it (or a while has passed).
  const [saved, setSaved] = useState<Needs | null>(null);
  const shown = useRef<HTMLLIElement>(null);
  const [refocus, setRefocus] = useState(false);
  const needs = trip.needs ?? [];
  const heard = needs.find((n) => n.person === trip.you) ?? { person: trip.you };
  const mine = saved ?? heard;
  useEffect(() => {
    if (saved && (saved.diet ?? "") === (heard.diet ?? "") && (saved.mobility ?? "") === (heard.mobility ?? ""))
      setSaved(null);
  }, [saved, heard.diet, heard.mobility]);
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(null), 5000);
    return () => clearTimeout(timer);
  }, [saved]);
  useEffect(() => {
    if (!refocus || editing) return;
    setRefocus(false);
    shown.current?.querySelector<HTMLElement>(".sv-btn")?.focus({ preventScroll: true });
  }, [refocus, editing]);
  const me = trip.members.find((m) => m.id === trip.you);
  // The others who said theirs, by name.
  const others = trip.members
    .filter((m) => m.id !== trip.you)
    .flatMap((m) => {
      const said = needs.find((n) => n.person === m.id);
      return said ? [{ person: m, said }] : [];
    })
    .sort((a, b) => a.person.name.localeCompare(b.person.name));
  return (
    <SettingsGroup id="needs-title" title="Diet and mobility">
      <div className="tm-settings-part tm-needs">
        <p className="caption tm-muted">
          For planning meals and walks around everyone. Each person sets their own here, and no AI sees them.
        </p>
        <ul className="tm-needs-people">
          {me &&
            (editing ? (
              <li className="tm-needs-person is-editing">
                <NeedsForm
                  trip={trip}
                  mine={mine}
                  onChanged={onChanged}
                  onClose={(now) => {
                    setEditing(false);
                    setRefocus(true);
                    if (now) setSaved(now);
                  }}
                />
              </li>
            ) : (
              <li className="tm-needs-person" ref={shown}>
                <Avatar name={me.name} color={me.color} {...(me.src ? { src: me.src } : {})} size={32} />
                <span className="tm-needs-words">
                  <span className="callout">You</span>
                  <NeedsLine said={mine} none="Nothing said yet" />
                </span>
                {!readOnly && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="tm-needs-edit"
                    aria-label="Edit your diet and mobility"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </Button>
                )}
              </li>
            ))}
          {others.map(({ person, said }) => (
            <li key={person.id} className="tm-needs-person">
              <Avatar name={person.name} color={person.color} {...(person.src ? { src: person.src } : {})} size={32} />
              <span className="tm-needs-words">
                <span className="callout">{person.name}</span>
                <NeedsLine said={said} none="" />
              </span>
            </li>
          ))}
        </ul>
        {trip.members.length > 1 && others.length === 0 && (
          <p className="caption tm-muted">Nobody else has said theirs yet.</p>
        )}
      </div>
    </SettingsGroup>
  );
}

// "Diet: vegetarian · Mobility: no long hikes", or what's said when there's neither.
function NeedsLine({ said, none }: { said: Partial<Needs>; none: string }) {
  if (!said.diet && !said.mobility) return none ? <span className="caption tm-muted">{none}</span> : null;
  return (
    <span className="caption tm-muted tm-needs-said">
      {factLine([said.diet ? `Diet: ${said.diet}` : null, said.mobility ? `Mobility: ${said.mobility}` : null])}
    </span>
  );
}

function NeedsForm({
  trip,
  mine,
  onChanged,
  onClose,
}: {
  trip: TripView;
  mine: Partial<Needs>;
  onChanged: () => void;
  // Closed; saved, with what it says now.
  onClose: (saved: Needs | null) => void;
}) {
  const id = useId();
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const [diet, setDiet] = useState(mine.diet ?? "");
  const [mobility, setMobility] = useState(mine.mobility ?? "");
  const growDiet = useGrow(diet);
  const growMobility = useGrow(mobility);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    form.current?.querySelector<HTMLElement>("textarea")?.focus({ preventScroll: true });
  }, []);

  const save = async () => {
    if (offline || busy) return;
    const next = { diet: diet.trim(), mobility: mobility.trim() };
    // Only what changed: one said before as a list or notes is left as it was unless it's edited.
    const change: { diet?: string | null; mobility?: string | null } = {};
    if (next.diet !== (mine.diet ?? "")) change.diet = next.diet || null;
    if (next.mobility !== (mine.mobility ?? "")) change.mobility = next.mobility || null;
    if (Object.keys(change).length === 0) return onClose(null);
    setError(null);
    setBusy(true);
    try {
      const done = await api.setNeeds(trip.id, change);
      onChanged();
      const what = Object.keys(change).join(" and ");
      if (done.change) say({ change: done.change, text: `Saved your ${what}` });
      onClose({
        person: trip.you,
        ...(next.diet ? { diet: next.diet } : {}),
        ...(next.mobility ? { mobility: next.mobility } : {}),
      });
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Couldn't save them. Try again.");
    }
  };

  return (
    <form
      ref={form}
      className="tm-edit-form tm-needs-form"
      aria-labelledby={`${id}-title`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onClose(null);
      }}
    >
      <h3 id={`${id}-title`} className="tm-hidden">
        Your diet and mobility
      </h3>
      <div className="sv-field tm-booking-field">
        <label className="sv-field-label" htmlFor={`${id}-diet`}>
          Diet
        </label>
        <textarea
          ref={growDiet}
          id={`${id}-diet`}
          className="sv-field-input tm-needs-input"
          rows={2}
          value={diet}
          maxLength={MAX_NEED}
          placeholder="Vegetarian, no shellfish…"
          onChange={(e) => setDiet(e.target.value)}
        />
      </div>
      <div className="sv-field tm-booking-field">
        <label className="sv-field-label" htmlFor={`${id}-mobility`}>
          Mobility
        </label>
        <textarea
          ref={growMobility}
          id={`${id}-mobility`}
          className="sv-field-input tm-needs-input"
          rows={2}
          value={mobility}
          maxLength={MAX_NEED}
          placeholder="No long hikes, a wheelchair…"
          onChange={(e) => setMobility(e.target.value)}
        />
      </div>
      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: saving needs signal.</span>
        </p>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      <span className="tm-note-actions">
        <Button variant="primary" size="sm" type="submit" disabled={busy || offline}>
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button variant="quiet" size="sm" type="button" onClick={() => onClose(null)}>
          Cancel
        </Button>
      </span>
    </form>
  );
}
